/**
 * Session state and permission helpers.
 *
 * The permission set here decides what to *render*. It never decides what is
 * *allowed* — the server re-evaluates every permission on every request, so a
 * user who edits this state in their console gains nothing but a button that
 * returns 403.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Permission, RoleKey, Scope } from '@lto/contracts';

import { api, restoreSession, setAccessToken } from './api';

export interface SessionUser {
  readonly id: string;
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly officeId: string;
  readonly role: RoleKey;
  readonly mustChangePassword: boolean;
}

interface LoginResponse {
  readonly accessToken: string;
  readonly expiresIn: number;
  readonly user: SessionUser;
  readonly permissions: ReadonlyArray<[Permission, Scope]>;
}

interface MeResponse {
  readonly userId: string;
  readonly officeId: string;
  readonly role: RoleKey;
  readonly mustChangePassword: boolean;
  readonly permissions: ReadonlyArray<[Permission, Scope]>;
}

interface SessionState {
  readonly user: SessionUser | null;
  readonly permissions: ReadonlyMap<Permission, Scope>;
  readonly status: 'restoring' | 'authenticated' | 'anonymous';
  readonly signIn: (email: string, password: string) => Promise<void>;
  readonly signOut: () => Promise<void>;
  readonly can: (permission: Permission) => boolean;
  readonly scopeFor: (permission: Permission) => Scope | undefined;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [permissions, setPermissions] = useState<ReadonlyMap<Permission, Scope>>(new Map());
  const [status, setStatus] = useState<SessionState['status']>('restoring');

  /**
   * On load, try the refresh cookie. This is what keeps a reload from throwing
   * the user back to the sign-in screen: the access token lives only in memory
   * and is gone, but the cookie survives.
   */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const restored = await restoreSession();
      if (cancelled) return;

      if (!restored) {
        setStatus('anonymous');
        return;
      }

      try {
        const me = await api.get<MeResponse>('/api/v1/auth/me');
        if (cancelled) return;
        setPermissions(new Map(me.permissions));

        // /me is authoritative for role, office and the password flag — the
        // flag in particular may have been set by an administrator since this
        // session began, so a restored session must pick it up rather than
        // keep whatever sign-in reported.
        setUser((current) =>
          current === null
            ? {
                id: me.userId,
                email: '',
                firstName: '',
                lastName: '',
                officeId: me.officeId,
                role: me.role,
                mustChangePassword: me.mustChangePassword,
              }
            : {
                ...current,
                officeId: me.officeId,
                role: me.role,
                mustChangePassword: me.mustChangePassword,
              },
        );
        setStatus('authenticated');
      } catch {
        if (!cancelled) setStatus('anonymous');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const session = await api.post<LoginResponse>('/api/v1/auth/login', { email, password });
    setAccessToken(session.accessToken);
    setUser(session.user);
    setPermissions(new Map(session.permissions));
    setStatus('authenticated');
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post('/api/v1/auth/logout');
    } catch {
      // Signing out locally must succeed even if the server call does not.
    }
    setAccessToken(null);
    setUser(null);
    setPermissions(new Map());
    setStatus('anonymous');
  }, []);

  const value = useMemo<SessionState>(
    () => ({
      user,
      permissions,
      status,
      signIn,
      signOut,
      can: (permission) => permissions.has(permission),
      scopeFor: (permission) => permissions.get(permission),
    }),
    [user, permissions, status, signIn, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const context = useContext(SessionContext);
  if (context === null) {
    throw new Error('useSession must be used within a SessionProvider.');
  }
  return context;
}
